import { useCallback, useEffect, useRef, useState } from "react";
import { Loader } from "@mantine/core";
import { verifyUploadToken, submitUploadImages } from "../../services/smsUpload";

// ─── Image compression (mirrors AddNewMedicationModal logic) ─────────────────
const compressImage = (file) =>
  new Promise((resolve) => {
    const MAX_WIDTH = 1920;
    const QUALITY = 0.82;
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      if (width > MAX_WIDTH) {
        height = Math.round((height * MAX_WIDTH) / width);
        width = MAX_WIDTH;
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) =>
          resolve(
            new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
              type: "image/jpeg",
            }),
          ),
        "image/jpeg",
        QUALITY,
      );
    };
    img.src = objectUrl;
  });

// ─── Page states ─────────────────────────────────────────────────────────────
const STATE = {
  LOADING: "loading",
  EXPIRED: "expired",
  REVOKED: "revoked",
  INVALID: "invalid",
  READY: "ready",
  SUBMITTING: "submitting",
  SUCCESS: "success",
};

let _imageIdCounter = 0;
const nextId = () => String(++_imageIdCounter);

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#F5F5F5",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "24px 16px 48px",
    fontFamily: "Inter, -apple-system, Segoe UI, sans-serif",
  },
  logo: {
    marginBottom: 24,
    textAlign: "center",
  },
  card: {
    width: "100%",
    maxWidth: 480,
    background: "#FFFFFF",
    borderRadius: 16,
    border: "1px solid #E5E5E5",
    padding: 24,
    boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)",
  },
  heading: {
    margin: "0 0 4px",
    fontSize: 18,
    fontWeight: 700,
    color: "#171717",
    lineHeight: 1.3,
  },
  sub: {
    margin: "0 0 20px",
    fontSize: 13,
    color: "#525252",
    lineHeight: 1.5,
  },
  sectionLabel: {
    margin: "0 0 8px",
    fontSize: 11,
    fontWeight: 700,
    color: "#737373",
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  uploadArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    padding: "20px 16px",
    border: "1.5px dashed #D0D5DD",
    borderRadius: 10,
    background: "#FAFAFA",
    cursor: "pointer",
    textAlign: "center",
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 40,
    padding: "0 18px",
    borderRadius: 8,
    background: "#6941C6",
    color: "#FFFFFF",
    border: "none",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Inter",
    cursor: "pointer",
    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
  },
  btnSecondary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 40,
    padding: "0 18px",
    borderRadius: 8,
    background: "#FFFFFF",
    color: "#1D2939",
    border: "1px solid #D0D5DD",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Inter",
    cursor: "pointer",
    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
  },
  btnSubmit: {
    width: "100%",
    height: 48,
    borderRadius: 10,
    background: "#FFAF4A",
    color: "#262626",
    border: "none",
    fontSize: 15,
    fontWeight: 700,
    fontFamily: "Inter",
    cursor: "pointer",
    marginTop: 20,
    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
  },
  imageCard: {
    display: "flex",
    gap: 10,
    padding: "10px",
    borderRadius: 10,
    border: "1px solid #E5E5E5",
    background: "#FAFAFA",
    marginBottom: 8,
  },
  thumb: {
    width: 72,
    height: 72,
    borderRadius: 8,
    objectFit: "cover",
    border: "1px solid #E5E5E5",
    flexShrink: 0,
  },
  noteInput: {
    flex: 1,
    border: "1px solid #D0D5DD",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 12,
    fontFamily: "Inter",
    color: "#374151",
    outline: "none",
    resize: "none",
    background: "#FFFFFF",
    marginTop: 4,
  },
  removeBtn: {
    background: "transparent",
    border: 0,
    color: "#9CA3AF",
    cursor: "pointer",
    padding: "2px 4px",
    fontSize: 16,
    lineHeight: 1,
    flexShrink: 0,
  },
  errorIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    margin: "0 auto 16px",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
const SmsUploadPage = () => {
  const token = new URLSearchParams(window.location.search).get("token");
  const [pageState, setPageState] = useState(STATE.LOADING);
  const [clientFirstName, setClientFirstName] = useState("");
  const [images, setImages] = useState([]);
  const [submitError, setSubmitError] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setPageState(STATE.INVALID);
      return;
    }
    verifyUploadToken(token)
      .then((data) => {
        setClientFirstName(data?.clientFirstName || "");
        setPageState(STATE.READY);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message?.toLowerCase() || "";
        if (msg.includes("expired")) setPageState(STATE.EXPIRED);
        else if (msg.includes("revoked")) setPageState(STATE.REVOKED);
        else setPageState(STATE.INVALID);
      });
  }, [token]);

  // iOS Safari needs srcObject assigned after React commits the <video> element.
  // setTimeout(0) fires before the commit and causes a black screen; useEffect
  // runs post-commit so the element is always ready. play() is also required on iOS.
  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen]);

  // ── Camera helpers ────────────────────────────────────────────────────────
  const openCamera = useCallback(async (facing = "environment") => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing } },
      });
      streamRef.current = stream;
      setFacingMode(facing);
      setCameraOpen(true); // useEffect above assigns srcObject + calls play()
    } catch {
      // fall back to native file input with capture
      if (fileInputRef.current) {
        fileInputRef.current.setAttribute("capture", "environment");
        fileInputRef.current.click();
      }
    }
  }, []);

  const closeCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  }, []);

  const switchCamera = useCallback(async () => {
    const newFacing = facingMode === "environment" ? "user" : "environment";
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: newFacing } },
      });
      streamRef.current = stream;
      setFacingMode(newFacing);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch {
      // ignore
    }
  }, [facingMode]);

  const captureFromCamera = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(video, 0, 0, width, height);
    canvas.toBlob(async (blob) => {
      if (!blob || blob.size < 5000) return;
      closeCamera();
      let file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
      if (file.size > 2 * 1024 * 1024) file = await compressImage(file);
      const preview = URL.createObjectURL(file);
      setImages((prev) => {
        prev.forEach((img) => URL.revokeObjectURL(img.preview));
        return [{ id: nextId(), file, preview, note: "" }];
      });
    }, "image/jpeg", 0.92);
  }, [closeCamera]);

  // ── File input handler ────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    let file = picked[0];
    if (file.size > 2 * 1024 * 1024) file = await compressImage(file);
    const entry = { id: nextId(), file, preview: URL.createObjectURL(file), note: "" };
    setImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.preview));
      return [entry];
    });
    e.target.value = "";
    e.target.removeAttribute("capture");
  };

  const updateNote = (id, note) =>
    setImages((prev) => prev.map((img) => (img.id === id ? { ...img, note } : img)));

  const removeImage = (id) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img?.preview) URL.revokeObjectURL(img.preview);
      return prev.filter((i) => i.id !== id);
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!images.length) {
      setSubmitError("Please add at least one photo before submitting.");
      return;
    }
    setSubmitError("");
    setPageState(STATE.SUBMITTING);
    try {
      const formData = new FormData();
      images.forEach((img, i) => {
        formData.append("images", img.file);
        if (img.note.trim()) formData.append(`note_${i}`, img.note.trim());
      });
      await submitUploadImages(token, formData);
      setPageState(STATE.SUCCESS);
    } catch (err) {
      const msg = err?.response?.data?.message?.toLowerCase() || "";
      if (msg.includes("expired")) {
        setPageState(STATE.EXPIRED);
      } else {
        setSubmitError(err?.response?.data?.message || "Submission failed. Please try again.");
        setPageState(STATE.READY);
      }
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderErrorState = (icon, iconBg, iconStroke, title, body) => (
    <div style={{ ...S.card, textAlign: "center" }}>
      <div style={{ ...S.errorIcon, background: iconBg }}>
        {icon(iconStroke)}
      </div>
      <p style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: "#171717", fontFamily: "Inter" }}>
        {title}
      </p>
      <p style={{ margin: 0, fontSize: 13, color: "#525252", fontFamily: "Inter", lineHeight: 1.5 }}>
        {body}
      </p>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Brand */}
      <div style={S.logo}>
        <img src="/assets/images/logo2.png" alt="Ogiso" style={{ height: 40, objectFit: "contain" }} />
      </div>

      {/* Loading */}
      {pageState === STATE.LOADING && (
        <div style={{ ...S.card, textAlign: "center", padding: 40 }}>
          <Loader color="violet" size="md" />
          <p style={{ margin: "16px 0 0", fontSize: 13, color: "#525252", fontFamily: "Inter" }}>
            Verifying your link…
          </p>
        </div>
      )}

      {/* Expired */}
      {pageState === STATE.EXPIRED &&
        renderErrorState(
          (c) => (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          ),
          "#FEF3C7", "#D97706",
          "Link expired",
          "This upload link has expired. Please ask the person who sent it to generate a new one.",
        )}

      {/* Revoked */}
      {pageState === STATE.REVOKED &&
        renderErrorState(
          (c) => (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
          ),
          "#FEE2E2", "#DC2626",
          "Link revoked",
          "This upload link has been cancelled. Please contact the sender to request a new link.",
        )}

      {/* Invalid */}
      {pageState === STATE.INVALID &&
        renderErrorState(
          (c) => (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          ),
          "#F3F4F6", "#6B7280",
          "Invalid link",
          "This link is not valid. Please check that you opened the correct URL.",
        )}

      {/* Success */}
      {pageState === STATE.SUCCESS && (
        <div style={{ ...S.card, textAlign: "center" }}>
          <div style={{ ...S.errorIcon, background: "#DCFCE7" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <p style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: "#171717", fontFamily: "Inter" }}>
            Photos submitted!
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "#525252", fontFamily: "Inter", lineHeight: 1.5 }}>
            Thank you! Your photos have been sent securely and will be reviewed shortly.
          </p>
        </div>
      )}

      {/* Upload form */}
      {(pageState === STATE.READY || pageState === STATE.SUBMITTING) && (
        <div style={S.card}>
          <p style={S.heading}>
            Upload medications{clientFirstName ? ` for ${clientFirstName}` : ""}
          </p>
          <p style={S.sub}>
            Take or upload clear photos of each medication bottle or prescription label.
            Include all sides showing the medication name, dosage, and instructions.
          </p>

          {/* Upload buttons */}
          <p style={S.sectionLabel}>Add photo</p>
          <div style={S.uploadArea}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
              <button type="button" style={S.btnPrimary} onClick={() => openCamera()}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                Take photo
              </button>
              <button
                type="button"
                style={S.btnSecondary}
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.removeAttribute("capture");
                    fileInputRef.current.click();
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 16 12 12 8 16" />
                  <line x1="12" y1="12" x2="12" y2="21" />
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                </svg>
                Upload file
              </button>
            </div>
            <p style={{ margin: 0, fontSize: 11, color: "#9CA3AF", fontFamily: "Inter" }}>
              JPEG, PNG, or PDF files
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif,application/pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* Image list */}
          {images.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ ...S.sectionLabel, marginBottom: 10 }}>
                Photo added
              </p>
              {images.map((img) => (
                <div key={img.id} style={S.imageCard}>
                  <img src={img.preview} alt="medication" style={S.thumb} />
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                      <p style={{ margin: 0, fontSize: 12, color: "#737373", fontFamily: "Inter" }}>
                        Optional note
                      </p>
                      <button
                        type="button"
                        style={S.removeBtn}
                        onClick={() => removeImage(img.id)}
                        aria-label="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                    <textarea
                      value={img.note}
                      onChange={(e) => updateNote(img.id, e.target.value)}
                      placeholder='e.g. "morning meds"'
                      rows={2}
                      maxLength={120}
                      style={S.noteInput}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {submitError && (
            <p style={{ margin: "12px 0 0", fontSize: 13, color: "#DC2626", fontFamily: "Inter" }}>
              {submitError}
            </p>
          )}

          {/* Submit */}
          <button
            type="button"
            style={{
              ...S.btnSubmit,
              opacity: pageState === STATE.SUBMITTING ? 0.7 : 1,
              cursor: pageState === STATE.SUBMITTING ? "not-allowed" : "pointer",
            }}
            onClick={handleSubmit}
            disabled={pageState === STATE.SUBMITTING}
          >
            {pageState === STATE.SUBMITTING ? "Submitting…" : "Submit photos"}
          </button>

          <p style={{ margin: "12px 0 0", fontSize: 11, color: "#9CA3AF", fontFamily: "Inter", textAlign: "center", lineHeight: 1.5 }}>
            Your photos are encrypted in transit. No account required.
          </p>
        </div>
      )}

      {/* ── Webcam overlay ─────────────────────────────────────────────────── */}
      {cameraOpen && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.88)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 16,
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", maxWidth: 540, borderRadius: 12, background: "#000" }}
          />
          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="button"
              onClick={captureFromCamera}
              style={{
                height: 44, padding: "0 28px", borderRadius: 8,
                background: "#6941C6", color: "#fff", border: "none",
                fontSize: 14, fontWeight: 600, fontFamily: "Inter", cursor: "pointer",
              }}
            >
              Capture
            </button>
            <button
              type="button"
              onClick={switchCamera}
              style={{
                height: 44, padding: "0 16px", borderRadius: 8,
                background: "transparent", color: "#fff", border: "1px solid #fff",
                fontSize: 14, fontWeight: 600, fontFamily: "Inter", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 4v6h6" /><path d="M23 20v-6h-6" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
              </svg>
              Flip
            </button>
            <button
              type="button"
              onClick={closeCamera}
              style={{
                height: 44, padding: "0 28px", borderRadius: 8,
                background: "transparent", color: "#fff", border: "1px solid #fff",
                fontSize: 14, fontWeight: 600, fontFamily: "Inter", cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsUploadPage;
